import { message } from 'antd';
import RestCrudThunks from './restCrudThunksGenerator';
import FetchUtils, { parseJSON } from './fetch_utils';
import { getBackendUri } from '../utils/EnvConfigUtils';
import { FILTER_CONFIG_NAMES, ROLE_TYPES } from '../utils/DataConstants';
import {
	createLabelCatalogueSyncThunk,
	getDeletedLabelIds,
	LabelCatalogueEntry,
} from './label_catalogue_sync';
import { getRecordsForTargetCollection } from '../reducers/standard_reducer_utils';
import { nullSafeGetOrElse } from '../utils/DataAccessUtils';
import { buildLabelFilterRemovalPatch } from '../utils/FilterUtils';
import { updateFiltersWorkOrders } from './work_orders_thunks_wrapper';
import { logoutSuccess } from '../actions/session_actions';
import { WORK_ORDER_LABELS_CRUD_ACTION_CREATORS } from '../actions/work_order_labels_actions';
import { WORK_ORDERS_CRUD_ACTION_CREATORS } from '../actions/work_orders_actions';

const WORK_ORDER_LABELS_INDEX_TC = 'workOrderLabelsIndex';

const fetchUtils = new FetchUtils();

// ---------------------------------------------------------------------------
// Label catalogue CRUD — list / create / update / delete.
// The generator's URL shapes match the backend exactly:
//   GET/POST    /api/v1/{role}/work_order/work_order_labels
//   PUT         /api/v1/{role}/work_order/work_order_labels        (id in body)
//   GET/DELETE  /api/v1/{role}/work_order/work_order_labels/:id
// ---------------------------------------------------------------------------

export const workOrderLabelsRestCrudThunksForBuyer = new RestCrudThunks(
	'work_order_labels',
	'work_order_label',
	'workOrderLabel',
	'workOrderLabels',
	WORK_ORDER_LABELS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.BUYER,
	false,
	'work_order/'
);
export const workOrderLabelsRestCrudThunksForSupplier = new RestCrudThunks(
	'work_order_labels',
	'work_order_label',
	'workOrderLabel',
	'workOrderLabels',
	WORK_ORDER_LABELS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.SUPPLIER,
	false,
	'work_order/'
);

export const workOrderLabelsRestCrudThunks = (userType) =>
	userType === ROLE_TYPES.SUPPLIER
		? workOrderLabelsRestCrudThunksForSupplier
		: workOrderLabelsRestCrudThunksForBuyer;

// `labelIds` is the complete desired set the modal sent, so it decides membership; the response
// mappings and the catalogue only supply the label objects to render.
const resolveAssignedLabels = (state, labelIds: number[], mappings: any) => {
	const labelById = new Map<number, any>();
	(Array.isArray(mappings) ? mappings : []).forEach((mapping) => {
		const label = mapping && mapping.workOrderLabel;
		if (label && label.id !== undefined) labelById.set(Number(label.id), label);
	});
	getRecordsForTargetCollection(state.work_order_labels, WORK_ORDER_LABELS_INDEX_TC).forEach(
		(label: any) => {
			if (label && label.id !== undefined && !labelById.has(Number(label.id))) {
				labelById.set(Number(label.id), label);
			}
		}
	);
	return (labelIds || []).map((id) => labelById.get(Number(id))).filter(Boolean);
};

// ---------------------------------------------------------------------------
// Label assignment — PUT /work_order/work_orders/:woId/labels  { ids: [...] }
// Returns OkResponseSeq[WorkOrderLabelMapping]. This is not a standard CRUD
// route, so it is a hand-written thunk rather than part of the generator.
// ---------------------------------------------------------------------------
const updateWorkOrderLabelsForRole =
	(roleType: string) => (workOrderId: number | string, labelIds: number[]) => {
		const apiUrl = `${getBackendUri()}/api/v1/${roleType}/work_order/work_orders/${workOrderId}/labels`;
		return (dispatch, getState) =>
			new Promise((resolve, reject) => {
				fetchUtils
					.put(apiUrl, {
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
						data: { ids: labelIds },
					})
					.then(parseJSON)
					.then((result) => {
						const status = result.status;
						const ok = result.ok;
						const json = result.json;
						if (status === 401) {
							dispatch(logoutSuccess());
						}
						if (!ok || (json && json.status === 'error')) {
							message.error('Failed to update labels for this work order.');
							reject(
								json && json.message ? json.message : 'Failed to update labels for this work order.'
							);
						} else {
							// Patch rather than readOne: readOne creates a `records[id]` entry, which then
							// shadows the `searchRecords` this row's search-backed list actually writes.
							dispatch(
								WORK_ORDERS_CRUD_ACTION_CREATORS.updatePartialSuccess({
									id: workOrderId,
									labels: resolveAssignedLabels(getState(), labelIds, json && json.data),
								})
							);
							resolve(json && json.data !== undefined ? json.data : json);
						}
					})
					.catch((error) => {
						message.error('Failed to update labels for this work order.');
						reject(error && error.message);
					});
			});
	};

export const updateWorkOrderLabelsForBuyer = updateWorkOrderLabelsForRole(ROLE_TYPES.BUYER);
export const updateWorkOrderLabelsForSupplier = updateWorkOrderLabelsForRole(ROLE_TYPES.SUPPLIER);

export const updateWorkOrderLabels = (userType) =>
	userType === ROLE_TYPES.SUPPLIER
		? updateWorkOrderLabelsForSupplier
		: updateWorkOrderLabelsForBuyer;

// Cached work orders keep their own copy of the labels applied to them, so a deleted label lingers
// on the row until a page reload. Drop it from every cached record.
export const removeDeletedLabelsFromWorkOrders =
	(deletedLabelIds: number[]) => (dispatch, getState) => {
		const deleted = new Set(deletedLabelIds.map(Number));
		const slice = getState().work_orders;
		const patched = new Set();
		[slice.records, slice.searchRecords].forEach((recordMap) => {
			Object.keys(recordMap || {}).forEach((recordId) => {
				if (patched.has(recordId)) return;
				const labels = recordMap[recordId] && recordMap[recordId].labels;
				if (!Array.isArray(labels)) return;
				const kept = labels.filter((label) => !deleted.has(Number(label && label.id)));
				if (kept.length === labels.length) return;
				patched.add(recordId);
				dispatch(
					WORK_ORDERS_CRUD_ACTION_CREATORS.updatePartialSuccess({
						id: Number(recordId),
						labels: kept,
					})
				);
			});
		});
	};

const baseSyncWorkOrderLabelsForRole = createLabelCatalogueSyncThunk(
	(roleType) => `/api/v1/${roleType}/work_order/work_order_labels/sync`,
	'Failed to save labels. Please try again.',
	{
		getCatalogue: (state) =>
			getRecordsForTargetCollection(state.work_order_labels, WORK_ORDER_LABELS_INDEX_TC),
		onDeletedLabels: removeDeletedLabelsFromWorkOrders,
	}
);

// Deleting a label must also unpin it from any active Labels filter, or the list stays filtered
// on an id that matches nothing and renders empty. Renames need no filter change — the id is
// still valid — the catalogue refetch is what re-renders the pill.
const syncWorkOrderLabelsForRole =
	(roleType: string) =>
	(labels: LabelCatalogueEntry[], context: { history?: any; targetCollectionName?: string } = {}) =>
	(dispatch, getState) => {
		const catalogueBeforeSync = getRecordsForTargetCollection(
			getState().work_order_labels,
			WORK_ORDER_LABELS_INDEX_TC
		);
		return dispatch(baseSyncWorkOrderLabelsForRole(roleType)(labels)).then((result) => {
			const deletedLabelIds = getDeletedLabelIds(catalogueBeforeSync, labels);
			const { history, targetCollectionName } = context;
			if (!history || !targetCollectionName) return result;
			const filters = nullSafeGetOrElse(
				`${targetCollectionName}.filters`,
				getState().work_orders,
				{}
			);
			const patch = buildLabelFilterRemovalPatch(
				filters,
				deletedLabelIds,
				FILTER_CONFIG_NAMES.WO_LABELS
			);
			if (patch) {
				dispatch(
					updateFiltersWorkOrders(roleType)(true, history, targetCollectionName, patch, undefined)
				);
			}
			return result;
		});
	};

export const syncWorkOrderLabelsForBuyer = syncWorkOrderLabelsForRole(ROLE_TYPES.BUYER);
export const syncWorkOrderLabelsForSupplier = syncWorkOrderLabelsForRole(ROLE_TYPES.SUPPLIER);

export const syncWorkOrderLabels = (userType) =>
	userType === ROLE_TYPES.SUPPLIER ? syncWorkOrderLabelsForSupplier : syncWorkOrderLabelsForBuyer;
